import { shippingGiftCalculatorInit } from '../../cartDrawerFreeShippingCalculator/scripts';

let shippingModalInitialized = false;

export function initShippingModalFeature() {
  if (shippingModalInitialized) return;
  if (!document.querySelector('[shipping-modal-enabled]')) return;

  const start = () => {
    if (shippingModalInitialized) return;
    shippingModalInitialized = true;
    setupShippingModalFeature();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}

function setupShippingModalFeature() {
  const shippingAnnouncementElements = Array.from(document.querySelectorAll('[shipping-modal-enabled]'));
  if (!shippingAnnouncementElements.length) return;

  let countryMinFreeShipping = sessionStorage.getItem('CountryMinFreeShipping');
  let searchInitialized = false;

  const replaceVariables = (token, replacement) => {
    if (!token || replacement == null) return;

    shippingAnnouncementElements.forEach((wrapper) => {
      const announcementText = wrapper.querySelector('.announcement-bar__text');
      if (!announcementText || !announcementText.textContent.includes(token)) return;
      announcementText.textContent = announcementText.textContent.replace(token, replacement);
    });
  };

  const checkFreeShippingAmount = (matchesFilter, element) => {
    if (countryMinFreeShipping) {
      const formatted = Number(countryMinFreeShipping).toFixed(2);
      replaceVariables('[VALUE]', formatted);
      shippingGiftCalculatorInit();
    } else if (matchesFilter && element) {
      const amountElement = element.querySelector('.free.min-amount');
      if (!amountElement) return;

      const [amountValue] = amountElement.textContent.trim().split(' ');
      if (!amountValue) return;

      sessionStorage.setItem('CountryMinFreeShipping', amountValue);
      countryMinFreeShipping = amountValue;
      replaceVariables('[VALUE]', Number(amountValue).toFixed(2));
      shippingGiftCalculatorInit();
    }
  };

  const workingDays = window.shippingTranslationKeys?.['shipping_modal.shipping_table.working_days'] || '';
  const currencySymbols = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    DKK: 'DKK',
  };

  const getCurrencySymbol = () => {
    const activeCurrency = window.Shopify?.currency?.active;
    return currencySymbols[activeCurrency] || activeCurrency || '';
  };

  const buildShippingRow = (entry, includeCountry, rowSpan) => {
    const row = document.createElement('tr');
    row.classList.add('shipping-row');

    if (includeCountry) {
      const countryCell = document.createElement('th');
      countryCell.classList.add('country', 'col--country');
      countryCell.scope = 'rowgroup';
      countryCell.rowSpan = rowSpan;
      countryCell.textContent = entry.country || '';
      row.appendChild(countryCell);
    }

    const shippingCell = document.createElement('th');
    shippingCell.classList.add('shipping', 'col--shipping');
    shippingCell.scope = 'row';
    shippingCell.textContent = entry.shipping_name || '';
    row.appendChild(shippingCell);

    const deliveryCell = document.createElement('td');
    deliveryCell.classList.add('delivery', 'col--delivery');
    const deliveryDays = entry.days?.split(' ')[0] || '';
    deliveryCell.textContent = [deliveryDays, workingDays].filter(Boolean).join(' ').trim();
    row.appendChild(deliveryCell);

    const priceCell = document.createElement('td');
    priceCell.classList.add('price');
    const hasPrice = entry.price !== undefined && entry.price !== null && entry.price !== '';
    priceCell.textContent = hasPrice ? `${entry.price} ${getCurrencySymbol()}`.trim() : '';
    row.appendChild(priceCell);

    const freeCell = document.createElement('td');
    freeCell.classList.add('free');
    const hasFreeThreshold = entry.free_above !== undefined && entry.free_above !== null && entry.free_above !== '';
    if (entry.free_above === 'N/A') {
      freeCell.textContent = entry.free_above;
      freeCell.classList.remove('min-amount');
    } else if (hasFreeThreshold) {
      freeCell.classList.add('min-amount');
      freeCell.textContent = `${entry.free_above} ${getCurrencySymbol()}`.trim();
    }
    row.appendChild(freeCell);

    return row;
  };

  const toAmount = (value) => {
    if (value === undefined || value === null || value === '' || value === 'N/A') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  // Shopify keeps one rate per price band, so a method that is paid below its
  // free-shipping threshold arrives as TWO rows ("Royal Mail 8.0 / N/A" plus
  // "Royal Mail 0.0 / 90.0") - which rendered as a duplicated method, one of
  // them claiming to cost 0. The table already has a Price column and a Free
  // above column, so fold the bands into one row per method: cheapest paid
  // band as the price, lowest threshold as the free-above amount. Original
  // strings are carried over so the displayed formatting is unchanged.
  const mergeCountryEntries = (entries) => {
    const order = [];
    const merged = new Map();

    entries.forEach((entry) => {
      const key = `${entry.shipping_name || ''}||${entry.days || ''}`;
      if (!merged.has(key)) {
        order.push(key);
        merged.set(key, { entry: { ...entry }, price: null, priceRaw: '', free: null, freeRaw: 'N/A' });
      }
      const slot = merged.get(key);

      const price = toAmount(entry.price);
      if (price !== null && price > 0 && (slot.price === null || price < slot.price)) {
        slot.price = price;
        slot.priceRaw = entry.price;
      }

      const free = toAmount(entry.free_above);
      if (free !== null && (slot.free === null || free < slot.free)) {
        slot.free = free;
        slot.freeRaw = entry.free_above;
      }
    });

    // price '' = the method only exists above its free threshold, so no amount
    // applies below it; the Free above column carries the meaning on its own.
    return order.map((key) => {
      const slot = merged.get(key);
      return { ...slot.entry, price: slot.priceRaw, free_above: slot.freeRaw };
    });
  };

  const populateTable = (data) => {
    if (!Array.isArray(data) || !data.length) return;

    const table = document.querySelector('.shipping-prices-table');
    if (!table || table.dataset.populated === 'true') return;

    const templateBody = table.querySelector('tbody.country.tbody-country');
    if (!templateBody) return;

    templateBody.innerHTML = '';
    const fragment = document.createDocumentFragment();

    data.forEach((countryEntries, index) => {
      if (!Array.isArray(countryEntries) || !countryEntries.length) return;
      const targetBody = index === 0 ? templateBody : templateBody.cloneNode(false);
      targetBody.innerHTML = '';

      const entries = mergeCountryEntries(countryEntries);
      entries.forEach((entry, entryIndex) => {
        const row = buildShippingRow(entry, entryIndex === 0, entries.length);
        targetBody.appendChild(row);
      });

      if (index !== 0) fragment.appendChild(targetBody);
    });

    if (fragment.childNodes.length) {
      table.appendChild(fragment);
    }

    table.dataset.populated = 'true';
  };

  const setBodyVisibility = (tbody, shouldShow) => {
    tbody.style.display = shouldShow ? '' : 'none';
  };

  const toggleCountryDetails = (tbody) => {
    const paragraphs = tbody.querySelectorAll('p');
    paragraphs.forEach((paragraph) => {
      const isHidden = paragraph.hasAttribute('hidden');
      if (isHidden) {
        paragraph.removeAttribute('hidden');
        paragraph.style.display = '';
      } else {
        paragraph.setAttribute('hidden', 'true');
        paragraph.style.display = 'none';
      }
      paragraph.classList.toggle('el-content');
    });
  };

  const inputCountryCheck = () => {
    if (searchInitialized) return;

    const searchInput = document.getElementById('shipping-search');
    if (!searchInput) return;

    const countryBodies = Array.from(document.querySelectorAll('.shipping-info-container__table tbody.country'));
    if (!countryBodies.length) return;

    const updateVisibility = () => {
      const query = searchInput.value.trim().toLowerCase();
      let matches = 0;

      if (query.length > 2) {
        countryBodies.forEach((tbody) => {
          const text = tbody.textContent.toLowerCase();
          const isMatch = text.includes(query);
          setBodyVisibility(tbody, isMatch);
          if (isMatch) matches += 1;
          checkFreeShippingAmount(isMatch, tbody);
        });

        if (matches === 0) {
          countryBodies.forEach((tbody) => {
            const isRestOfWorld = tbody.textContent.toLowerCase().includes('rest of world');
            setBodyVisibility(tbody, isRestOfWorld);
            checkFreeShippingAmount(isRestOfWorld, tbody);
          });
        }
      } else {
        countryBodies.forEach((tbody) => {
          const isRestOfWorld = tbody.textContent.toLowerCase().includes('rest of world');
          setBodyVisibility(tbody, isRestOfWorld);
          checkFreeShippingAmount(isRestOfWorld, tbody);
        });
      }
    };

    ['input', 'change', 'focus', 'keyup'].forEach((eventName) => {
      searchInput.addEventListener(eventName, updateVisibility);
    });

    countryBodies.forEach((tbody) => {
      tbody.addEventListener('click', () => toggleCountryDetails(tbody));
    });

    searchInitialized = true;
    updateVisibility();
  };

  // If the placeholders were never resolved (no shipping row for the visitor's country,
  // rates endpoint down, etc.) swap the announcement to its neutral fallback text instead
  // of showing "Free shipping to Denmark above [VALUE] USD".
  const applyFallbackIfUnresolved = () => {
    shippingAnnouncementElements.forEach((wrapper) => {
      const announcementText = wrapper.querySelector('.announcement-bar__text');
      if (!announcementText) return;
      const txt = announcementText.textContent;
      if (!txt.includes('[VALUE]') && !txt.includes('[COUNTRY]') && !txt.includes('[CURRENCY]')) return;
      const fallback = wrapper.dataset.textFallback;
      if (fallback) announcementText.textContent = fallback;
    });
  };
  let fallbackTimer = null;
  const scheduleFallback = (ms = 2500) => {
    clearTimeout(fallbackTimer);
    fallbackTimer = setTimeout(applyFallbackIfUnresolved, ms);
  };

  const triggerSearchUpdate = () => {
    const searchInput = document.getElementById('shipping-search');
    if (searchInput) {
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    scheduleFallback();
  };

  const fetchLocationInformation = async () => {
    // Shopify's native geolocation suggestion endpoint (same-origin, free) -
    // replaces the former api.ipstack.com call and its paid access key.
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(
        `${root}browsing_context_suggestions.json?source=geolocation_recommendation&country%5Benabled%5D=true`
      );
      const data = await response.json();
      const countryName = data?.detected_values?.country_name || '';
      const countryCode = data?.detected_values?.country?.handle || '';
      const shippingSearch = document.getElementById('shipping-search');
      if (shippingSearch) {
        shippingSearch.value = countryName;
      }
      if (countryName) {
        sessionStorage.setItem('IPStackVisitorCountry', countryName);
        replaceVariables('[COUNTRY]', countryName);
        triggerSearchUpdate();
      }

      if (countryCode && countryCode !== window.Shopify.country) {
        document.querySelector('.country-redirect-modal')?.open();
      }
    } catch (error) {
      console.error('Unable to fetch location information', error);
    }
  };

  const fetchShippingRates = async () => {
    try {
      let data;
      const storedRates = sessionStorage.getItem('StoreShippingRates');

      if (storedRates) {
        data = JSON.parse(storedRates);
      } else {
        const store = window.Shopify?.shop || '';
        const response = await fetch(`https://netlify.blacklemon.dk/.netlify/functions/getShippingZones?store=${store}`);
        data = await response.json();
        sessionStorage.setItem('StoreShippingRates', JSON.stringify(data));
      }

      populateTable(data);
      inputCountryCheck();
      triggerSearchUpdate();
    } catch (error) {
      console.error('Unable to fetch shipping rates', error);
      applyFallbackIfUnresolved();
    }
  };

  const visitorCountry = sessionStorage.getItem('IPStackVisitorCountry');
  const shippingSearch = document.getElementById('shipping-search');

  if (visitorCountry && shippingSearch) {
    shippingSearch.value = visitorCountry;
    fetchShippingRates();
    replaceVariables('[COUNTRY]', visitorCountry);
    triggerSearchUpdate();
  } else {
    fetchLocationInformation();
    fetchShippingRates();
  }
  // safety net: never leave raw placeholders on screen
  scheduleFallback(6000);

  document.querySelectorAll('#shopify-section-announcement-bar .announcement-bar[shipping-modal-enabled]').forEach((el) => {
    el.addEventListener('click', () => {
      const modal = document.querySelector('.shipping-modal');
      if (modal?.open) modal.open();
    });
  });
}
